import MidiWriter from 'midi-writer-js';
import type { Keyboard, Scale } from './types';
import { frequencyForNote, freqToMidi, centsToPitchBend, pitchBendToBytes } from './scale';

// ---------------------------------------------------------------------------
// General MIDI instrument names (128 programs, 16 channels)
// ---------------------------------------------------------------------------

export const GM_INSTRUMENTS: string[] = [
  // Piano (0-7)
  'Acoustic Grand Piano', 'Bright Acoustic Piano', 'Electric Grand Piano', 'Honky-tonk Piano',
  'Electric Piano 1', 'Electric Piano 2', 'Harpsichord', 'Clavinet',
  // Chromatic Percussion (8-15)
  'Celesta', 'Glockenspiel', 'Music Box', 'Vibraphone',
  'Marimba', 'Xylophone', 'Tubular Bells', 'Dulcimer',
  // Organ (16-23)
  'Drawbar Organ', 'Percussive Organ', 'Rock Organ', 'Church Organ',
  'Reed Organ', 'Accordion', 'Harmonica', 'Tango Accordion',
  // Guitar (24-31)
  'Acoustic Guitar (nylon)', 'Acoustic Guitar (steel)', 'Electric Guitar (jazz)', 'Electric Guitar (clean)',
  'Electric Guitar (muted)', 'Overdriven Guitar', 'Distortion Guitar', 'Guitar Harmonics',
  // Bass (32-39)
  'Acoustic Bass', 'Electric Bass (finger)', 'Electric Bass (pick)', 'Fretless Bass',
  'Slap Bass 1', 'Slap Bass 2', 'Synth Bass 1', 'Synth Bass 2',
  // Strings (40-47)
  'Violin', 'Viola', 'Cello', 'Contrabass',
  'Tremolo Strings', 'Pizzicato Strings', 'Orchestral Harp', 'Timpani',
  // Ensemble (48-55)
  'String Ensemble 1', 'String Ensemble 2', 'Synth Strings 1', 'Synth Strings 2',
  'Choir Aahs', 'Voice Oohs', 'Synth Voice', 'Orchestra Hit',
  // Brass (56-63)
  'Trumpet', 'Trombone', 'Tuba', 'Muted Trumpet',
  'French Horn', 'Brass Section', 'Synth Brass 1', 'Synth Brass 2',
  // Reed (64-71)
  'Soprano Sax', 'Alto Sax', 'Tenor Sax', 'Baritone Sax',
  'Oboe', 'English Horn', 'Bassoon', 'Clarinet',
  // Pipe (72-79)
  'Piccolo', 'Flute', 'Recorder', 'Pan Flute',
  'Blown Bottle', 'Shakuhachi', 'Whistle', 'Ocarina',
  // Synth Lead (80-87)
  'Lead 1 (square)', 'Lead 2 (sawtooth)', 'Lead 3 (calliope)', 'Lead 4 (chiff)',
  'Lead 5 (charang)', 'Lead 6 (voice)', 'Lead 7 (fifths)', 'Lead 8 (bass + lead)',
  // Synth Pad (88-95)
  'Pad 1 (new age)', 'Pad 2 (warm)', 'Pad 3 (polysynth)', 'Pad 4 (choir)',
  'Pad 5 (bowed)', 'Pad 6 (metallic)', 'Pad 7 (halo)', 'Pad 8 (sweep)',
  // Synth Effects (96-103)
  'FX 1 (rain)', 'FX 2 (soundtrack)', 'FX 3 (crystal)', 'FX 4 (atmosphere)',
  'FX 5 (brightness)', 'FX 6 (goblins)', 'FX 7 (echoes)', 'FX 8 (sci-fi)',
  // Ethnic (104-111)
  'Sitar', 'Banjo', 'Shamisen', 'Koto',
  'Kalimba', 'Bagpipe', 'Fiddle', 'Shanai',
  // Percussive (112-119)
  'Tinkle Bell', 'Agogo', 'Steel Drums', 'Woodblock',
  'Taiko Drum', 'Melodic Tom', 'Synth Drum', 'Reverse Cymbal',
  // Sound Effects (120-127)
  'Guitar Fret Noise', 'Breath Noise', 'Seashore', 'Bird Tweet',
  'Telephone Ring', 'Helicopter', 'Applause', 'Gunshot',
];

export const GM_INSTRUMENT_GROUPS: { name: string; start: number; end: number }[] = [
  { name: 'Piano', start: 0, end: 7 },
  { name: 'Chromatic Percussion', start: 8, end: 15 },
  { name: 'Organ', start: 16, end: 23 },
  { name: 'Guitar', start: 24, end: 31 },
  { name: 'Bass', start: 32, end: 39 },
  { name: 'Strings', start: 40, end: 47 },
  { name: 'Ensemble', start: 48, end: 55 },
  { name: 'Brass', start: 56, end: 63 },
  { name: 'Reed', start: 64, end: 71 },
  { name: 'Pipe', start: 72, end: 79 },
  { name: 'Synth Lead', start: 80, end: 87 },
  { name: 'Synth Pad', start: 88, end: 95 },
  { name: 'Synth Effects', start: 96, end: 103 },
  { name: 'Ethnic', start: 104, end: 111 },
  { name: 'Percussive', start: 112, end: 119 },
  { name: 'Sound Effects', start: 120, end: 127 },
];

// ---------------------------------------------------------------------------
// Piano roll note & track types
// ---------------------------------------------------------------------------

export interface PianoRollNote {
  id: string;
  /** Index into keyboard.keys — which key this note plays. */
  keyIndex: number;
  /** Start time in ticks (PPQ = 128 per quarter note). */
  startTick: number;
  /** Duration in ticks. */
  duration: number;
  /** MIDI velocity 1-127. */
  velocity: number;
}

export interface PianoRollTrack {
  id: string;
  name: string;
  /** MIDI channel 0-15. */
  channel: number;
  /** GM program number 0-127. */
  program: number;
  /** Is this track muted? */
  muted: boolean;
  notes: PianoRollNote[];
}

export interface PianoRollProject {
  keyboard: Keyboard;
  tracks: PianoRollTrack[];
  /** Beats per minute. */
  bpm: number;
  /** Number of beats per bar. */
  beatsPerBar: number;
}

// ---------------------------------------------------------------------------
// MIDI export
// ---------------------------------------------------------------------------

export const PPQ = 128;

interface MidiChannelState {
  currentBend: number; // last pitch bend value sent (-8192..8192)
  hasProgram: boolean;
  program: number;
  hasBendRange: boolean;
}

/**
 * Export a piano roll project to a MIDI file (Uint8Array).
 *
 * Microtonal implementation strategy:
 * - Each note's exact frequency is computed from the keyboard's scale.
 * - The nearest 12-TET MIDI note is used as the base note number.
 * - A pitch-bend event (14-bit, ±200 cent range) is sent before each note-on
 *   to bend the nearest semitone to the exact microtonal pitch.
 * - Because pitch-bend is per-channel, notes that overlap on the same channel
 *   and need different bends are automatically reassigned to spare channels
 *   (channels 0-15). This gives up to 16 simultaneous microtonal voices.
 * - RPN Pitch Bend Sensitivity (±2 semitones) is set at the start of each
 *   used channel.
 */
export function exportMidi(project: PianoRollProject): Uint8Array {
  const { keyboard, tracks, bpm, beatsPerBar } = project;
  const scale: Scale = keyboard.scale;

  const track = new MidiWriter.Track();
  track.setTempo(bpm);
  track.setTimeSignature(beatsPerBar, 4);
  track.addTrackName('Microtonal Forge Export');

  // Channel state for all 16 channels
  const channelStates: MidiChannelState[] = Array.from({ length: 16 }, () => ({
    currentBend: 0,
    hasProgram: false,
    program: 0,
    hasBendRange: false,
  }));

  // Build a sorted event list
  interface PendingEvent {
    tick: number;
    order: number; // for sorting: pitch-bend before note-on, note-off before note-on
    apply: () => void;
  }

  const events: PendingEvent[] = [];
  let eventOrder = 0;

  // Track which channels are free at a given tick
  // channelBusyUntil[channel] = tick when the channel's last note ends
  const channelBusyUntil: number[] = new Array(16).fill(0);

  for (const tr of tracks) {
    if (tr.muted) continue;
    const preferredChannel = tr.channel;

    for (const note of tr.notes) {
      const key = keyboard.keys[note.keyIndex];
      if (!key) continue;

      const freq = frequencyForNote(scale, key.degree, key.period);
      const { note: midiNote, cents } = freqToMidi(freq);
      const bendValue = centsToPitchBend(cents, 200); // ±200 cent range

      // Find a channel for this note
      let channel = preferredChannel;

      // Check if preferred channel is busy with a different bend
      const preferredBusy = channelBusyUntil[preferredChannel] > note.startTick;
      const preferredBendMatches = channelStates[preferredChannel].currentBend === bendValue;

      if (preferredBusy && !preferredBendMatches) {
        // Find a free channel (or one with matching bend)
        let foundChannel = -1;
        for (let ch = 0; ch < 16; ch++) {
          if (ch === preferredChannel) continue;
          const isFree = channelBusyUntil[ch] <= note.startTick;
          const bendMatches = channelStates[ch].currentBend === bendValue;
          if (isFree || bendMatches) {
            foundChannel = ch;
            break;
          }
        }
        if (foundChannel >= 0) {
          channel = foundChannel;
        }
        // If no channel found, use preferred (will have a brief bend conflict — acceptable for extreme polyphony)
      }

      const endTick = note.startTick + note.duration;
      channelBusyUntil[channel] = Math.max(channelBusyUntil[channel], endTick);

      const chState = channelStates[channel];

      // Set pitch bend range (RPN) if not yet done on this channel
      if (!chState.hasBendRange) {
        const tick = note.startTick;
        events.push({
          tick: Math.max(0, tick - 1),
          order: eventOrder++,
          apply: () => {
            // RPN MSB/LSB = 0 (Pitch Bend Sensitivity)
            track.addEvent(new MidiWriter.ControllerChangeEvent({ controllerNumber: 101, controllerValue: 0, channel: channel + 1 }));
            track.addEvent(new MidiWriter.ControllerChangeEvent({ controllerNumber: 100, controllerValue: 0, channel: channel + 1 }));
            // Data Entry MSB = 2 (±2 semitones)
            track.addEvent(new MidiWriter.ControllerChangeEvent({ controllerNumber: 6, controllerValue: 2, channel: channel + 1 }));
            // Data Entry LSB = 0
            track.addEvent(new MidiWriter.ControllerChangeEvent({ controllerNumber: 38, controllerValue: 0, channel: channel + 1 }));
            // Deselect RPN
            track.addEvent(new MidiWriter.ControllerChangeEvent({ controllerNumber: 101, controllerValue: 127, channel: channel + 1 }));
            track.addEvent(new MidiWriter.ControllerChangeEvent({ controllerNumber: 100, controllerValue: 127, channel: channel + 1 }));
          },
        });
        chState.hasBendRange = true;
      }

      // Set program change if needed
      if (!chState.hasProgram || chState.program !== tr.program) {
        events.push({
          tick: Math.max(0, note.startTick - 1),
          order: eventOrder++,
          apply: () => {
            track.addEvent(new MidiWriter.ProgramChangeEvent({ instrument: tr.program, channel: channel + 1 }));
          },
        });
        chState.hasProgram = true;
        chState.program = tr.program;
      }

      // Set pitch bend if different from current
      if (chState.currentBend !== bendValue) {
        events.push({
          tick: note.startTick,
          order: eventOrder++,
          apply: () => {
            const [lsb, msb] = pitchBendToBytes(bendValue);
            const bendEvent = new MidiWriter.PitchBendEvent({ bend: bendValue / 8192, channel: channel + 1 });
            // Ensure exact 14-bit value by setting data directly
            bendEvent.data = [0xe0 | channel, lsb, msb];
            track.addEvent(bendEvent);
          },
        });
        chState.currentBend = bendValue;
      }

      // Note on
      events.push({
        tick: note.startTick,
        order: eventOrder++,
        apply: () => {
          track.addEvent(new MidiWriter.NoteOnEvent({
            pitch: midiNote,
            velocity: Math.max(1, Math.min(127, note.velocity)),
            channel: channel + 1,
            tick: note.startTick,
          }));
        },
      });

      // Note off
      events.push({
        tick: endTick,
        order: eventOrder++,
        apply: () => {
          track.addEvent(new MidiWriter.NoteOffEvent({
            pitch: midiNote,
            velocity: 0,
            channel: channel + 1,
            duration: note.duration,
            tick: endTick,
          }));
        },
      });
    }
  }

  // Sort events by tick, then by order (pitch-bend/setup before note-on)
  events.sort((a, b) => a.tick - b.tick || a.order - b.order);

  // Apply events in order — midi-writer-js handles delta calculation
  for (const ev of events) {
    ev.apply();
  }

  const writer = new MidiWriter.Writer(track);
  return writer.buildFile();
}

/** Trigger a download of a Uint8Array as a file. */
export function downloadMidi(data: Uint8Array, filename: string): void {
  const blob = new Blob([data as BlobPart], { type: 'audio/midi' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
